import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { useAuth } from './useAuth.tsx';

interface TenantContextValue {
  /** Resolved tenant IDs the user can access. For super_admin, all tenants from /admin/tenants. */
  tenantIds: string[];
  /** Default active tenant (first in the list). */
  activeTenantId: string | null;
  /** Cognito sub of the logged-in admin. */
  adminId: string;
  /** True while resolving tenants via API (only happens for super_admin on first render). */
  loading: boolean;
}

/**
 * Resolves the effective list of tenant IDs the logged-in admin can access.
 *
 * - Scoped users: returns user.tenantScope from JWT claim (synchronous).
 * - Super_admin (empty tenantScope): fetches /admin/tenants once and returns
 *   the full list. Backend enforces scope, so it returns ALL tenants for
 *   super_admin. Mirrors the ws-authorizer pattern (FU-21).
 */
export function useTenantContext(): TenantContextValue {
  const { user, token } = useAuth();
  const explicitScope = user?.tenantScope ?? [];
  const isSuperAdmin = explicitScope.length === 0;

  const [resolved, setResolved] = useState<string[] | null>(isSuperAdmin ? null : explicitScope);

  useEffect(() => {
    if (!isSuperAdmin || !token) return;
    let cancelled = false;
    api.tenants
      .list()
      .then((res) => {
        if (cancelled) return;
        const ids = res.tenants.map((t) => t.tenantId);
        setResolved(ids);
      })
      .catch((err) => {
        console.warn('useTenantContext: failed to fetch tenants for super_admin', err);
        if (!cancelled) setResolved([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, token]);

  const tenantIds = resolved ?? [];
  const activeTenantId = tenantIds[0] ?? null;
  const adminId = user?.sub ?? '';

  return {
    activeTenantId,
    tenantIds,
    adminId,
    loading: resolved === null,
  };
}
